import React, { useState, useMemo } from 'react';
import classes from './RevenueCalculator.module.css';
import { ToggleButtonWithState } from '../../../components/Inputs/Input';

const BASELINES = {
    '4h': {
        DK1: { up: 4773899, down: 14158 },
        DK2: { up: 5048730, down: 382449 },
    },
    '2h': {
        DK1: { up: 3895251, down: 0 },
        DK2: { up: 4463858, down: 198670 },
    },
};

const fmt = (n) => {
    if (!isFinite(n)) return '—';
    return Math.round(n).toLocaleString('da-DK');
};

const fmtSigned = (n) => {
    if (!isFinite(n)) return '—';
    const abs = Math.abs(Math.round(n)).toLocaleString('da-DK');
    return n < 0 ? `(${abs})` : abs;
};

const DEFAULT_INPUTS = {
    capacityMW: 1,
    aggregatorPct: 10,
    declinePct: 5,
    tariffKr: 115,
    spotKr: 617,
    hoursUp: 2144,
    hoursDown: 2044,
    greenCreditPct: 75,
    arbSpread: 400,
    arbCycles: 250,
    serviceKr: 24566,
    insuranceKr: 49133,
};

const NumberInput = ({ label, hint, id, value, unit, onChange, step = 1, min = 0, max }) => (
    <div className={classes.fieldGroup}>
        <div className={classes.fieldLabel}>
            {label}
            {hint && <span className={classes.fieldHint}>{hint}</span>}
        </div>
        <div className={classes.inputRow}>
            <input
                type="number"
                className={classes.numberInput}
                value={value}
                min={min}
                step={step}
                {...(max !== undefined ? { max } : {})}
                onChange={(e) => onChange(id, e.target.value)}
                onWheel={(e) => e.target.blur()}
            />
            {unit && <span className={classes.unitLabel}>{unit}</span>}
        </div>
    </div>
);

const RevenueCalculator = () => {
    const [region, setRegion] = useState('DK2');
    const [duration, setDuration] = useState('4h');
    const [loadbanks, setLoadbanks] = useState(true);
    const [backupAlloc, setBackupAlloc] = useState(0);
    const [inputs, setInputs] = useState(DEFAULT_INPUTS);

    const setInput = (key, value) => setInputs((prev) => ({ ...prev, [key]: parseFloat(value) || 0 }));

    const results = useMemo(() => {
        const {
            capacityMW: mw, aggregatorPct, declinePct, tariffKr: tariff, spotKr: spot,
            hoursUp, hoursDown, greenCreditPct, arbSpread, arbCycles,
            serviceKr: serviceCost, insuranceKr: insuranceCost,
        } = inputs;

        const aggregator = aggregatorPct / 100;
        const decline = declinePct / 100;
        const greenCredit = greenCreditPct / 100;
        const effRatio = 0.85; // round-trip virkningsgrad (fast antagelse)
        const sysShare = 1 - backupAlloc / 100;
        const durationHours = duration === '4h' ? 4 : 2;

        const baseUp = BASELINES[duration][region].up * mw * sysShare;
        const baseDown = loadbanks ? BASELINES[duration][region].down * mw * sysShare : 0;
        const arbitrage = durationHours * arbSpread * effRatio * arbCycles * mw * sysShare;

        const nedregNetCost = duration === '4h' ? (3 * tariff * mw * hoursDown) : 0;
        const opCostNet = -(hoursUp * mw * tariff + nedregNetCost) * sysShare;
        const opCostEl = -(spot * hoursUp * mw) * sysShare;
        const greenBenefit = loadbanks ? -greenCredit * opCostEl : 0;

        const gross = baseUp + baseDown + arbitrage;
        const db1 = gross + opCostNet + opCostEl + greenBenefit;
        const aggregatorFee = -aggregator * db1;
        const serviceFee = -serviceCost * mw;
        const insuranceFee = -insuranceCost * mw;
        const db2 = db1 + aggregatorFee + serviceFee + insuranceFee;

        let cumulative = 0;
        for (let yr = 1; yr <= 10; yr++) {
            const f = Math.pow(1 - decline, yr - 1);
            const yrGross = (baseUp + baseDown + arbitrage) * f;
            const yrDb1 = yrGross + opCostNet + opCostEl + greenBenefit;
            const yrDb2 = yrDb1 + (-aggregator * yrDb1) + serviceFee + insuranceFee;
            cumulative += yrDb2;
        }

        const pnlRows = [
            { label: 'Indtægt: opregulering', value: baseUp, kind: 'normal' },
            { label: 'Indtægt: nedregulering', value: baseDown, kind: loadbanks ? 'normal' : 'disabled', note: loadbanks ? null : '— ingen loadbanks' },
            { label: 'Indtægt: arbitrage (spothandel)', value: arbitrage, kind: arbitrage > 0 ? 'normal' : 'disabled' },
            { label: 'Driftsomkostninger · nettrafik', value: opCostNet, kind: 'normal' },
            { label: 'Driftsomkostninger · elforbrug', value: opCostEl, kind: 'normal' },
            { label: 'Bidrag fra grøn opladning (nedreg.)', value: greenBenefit, kind: loadbanks ? 'normal' : 'disabled', note: loadbanks ? null : '— ingen loadbanks' },
            { label: 'Aggregator/drift', value: aggregatorFee, kind: 'normal' },
            { label: 'Service-aftale på udstyret', value: serviceFee, kind: 'normal' },
            { label: 'Diverse, forsikring m.m.', value: insuranceFee, kind: 'normal' },
            { label: 'Dækningsbidrag 2 (DB2) — netto-indtægt år 1', value: db2, kind: 'total' },
        ];

        return { baseUp, baseDown, db2, cumulative, pnlRows };
    }, [region, duration, loadbanks, backupAlloc, inputs]);

    const kpis = [
        { label: 'DB2', value: results.db2, sub: 'efter aggregator + service' },
        { label: '10-årig kum. netto', value: results.cumulative, sub: 'med årlig markedsnedgang' },
    ];

    return (
        <div className={classes.container}>
            <div className={classes.sourceLine}>
                Markedsdata til og med <strong>Juli 2026</strong> &middot; Kilde: Energinet.dk &middot; 12-mdr rullende
            </div>

            <div className={classes.hero}>
                <h2 className={classes.heroTitle}>Estimér indtægter fra systemydelser for BESS</h2>
                <p className={classes.heroText}>
                    Værktøjet hjælper partnere med at estimere de forventede årlige indtægter for et Battery Energy Storage System (BESS)
                    der deltager i de danske systemydelsesmarkeder (aFRR/PICASSO). Konfigurér projektet på venstre side og se opdaterede forecasts.
                </p>
            </div>

            <div className={classes.layout}>
                {/* LEFT: Configuration */}
                <div className={classes.configCard}>
                    <div className={classes.cardHeader}>
                        <span className={classes.cardNum}>01</span>
                        <span className={classes.cardTitle}>Projekt konfiguration</span>
                    </div>

                    <div className={classes.cardBody}>
                        {/* Region */}
                        <div className={classes.fieldGroup}>
                            <div className={classes.fieldLabel}>
                                Prisområde
                                <span className={classes.fieldHint}>DK1 = Vestdanmark &middot; DK2 = Østdanmark</span>
                            </div>
                            <div className={classes.segGroup}>
                                {['DK1', 'DK2'].map((r) => (
                                    <button
                                        key={r}
                                        onClick={() => setRegion(r)}
                                        className={region === r ? classes.segActive : classes.segInactive}
                                    >
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Capacity */}
                        <NumberInput label="BESS kapacitet" hint="Reference: 1 MW" id="capacityMW" value={inputs.capacityMW} unit="MW" step={0.1} min={0.1} onChange={setInput} />

                        {/* Duration */}
                        <div className={classes.fieldGroup}>
                            <div className={classes.fieldLabel}>
                                Batteri-løsning
                                <span className={classes.fieldHint}>4-timers = kapacitet + energi &middot; 2-timers = kun energi</span>
                            </div>
                            <div className={classes.segGroup}>
                                {[{ val: '4h', label: '4-timers' }, { val: '2h', label: '2-timers' }].map((d) => (
                                    <button
                                        key={d.val}
                                        onClick={() => setDuration(d.val)}
                                        className={duration === d.val ? classes.segActive : classes.segInactive}
                                    >
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Options */}
                        <div className={classes.sectionDivider}>
                            <span className={classes.sectionLabel}>Valgmuligheder</span>
                        </div>

                        <div className={classes.toggleRow}>
                            <div>
                                <div className={classes.toggleName}>Loadbanks installeret</div>
                                <div className={classes.toggleDesc}>Aktiverer indtægter fra nedregulering</div>
                            </div>
                            <ToggleButtonWithState
                                value={loadbanks}
                                onChange={() => setLoadbanks((v) => !v)}
                                defaultStyle
                            />
                        </div>

                        {/* Backup allocation slider */}
                        <div className={classes.sliderCard}>
                            <div className={classes.sliderHeader}>
                                <span className={classes.toggleName}>Allokering til nødstrøm</span>
                                <span className={classes.sliderValue}><strong>{backupAlloc}%</strong> reserveret</span>
                            </div>
                            <input
                                type="range"
                                className={classes.slider}
                                min={0} max={100} step={1}
                                value={backupAlloc}
                                style={{ '--fill': `${backupAlloc}%` }}
                                onChange={(e) => setBackupAlloc(Number(e.target.value))}
                            />
                            <div className={classes.sliderFooter}>
                                <span>Nødstrøm <strong>{backupAlloc}%</strong></span>
                                <span>Systemydelser <strong>{100 - backupAlloc}%</strong></span>
                            </div>
                        </div>

                        {/* Assumptions */}
                        <div className={classes.sectionDivider}>
                            <span className={classes.sectionLabel}>Drifts-antagelser</span>
                        </div>

                        <div className={classes.twoCol}>
                            <NumberInput label="Aggregator/drift" id="aggregatorPct" value={inputs.aggregatorPct} unit="%" max={100} onChange={setInput} />
                            <NumberInput label="Markedsnedgang/år" id="declinePct" value={inputs.declinePct} unit="%" step={0.5} max={50} onChange={setInput} />
                        </div>
                        <div className={classes.twoCol}>
                            <NumberInput label="Gns. tariffer" id="tariffKr" value={inputs.tariffKr} unit="kr/MWh" onChange={setInput} />
                            <NumberInput label="Gns. spotpris" id="spotKr" value={inputs.spotKr} unit="kr/MWh" onChange={setInput} />
                        </div>
                        <div className={classes.twoCol}>
                            <NumberInput label="Timer opregulering" id="hoursUp" value={inputs.hoursUp} unit="h" onChange={setInput} />
                            <NumberInput label="Timer nedregulering" id="hoursDown" value={inputs.hoursDown} unit="h" onChange={setInput} />
                        </div>
                        <div className={classes.twoCol}>
                            <NumberInput label="Bidrag fra grøn opladning" hint="andel af nedreg.-strøm" id="greenCreditPct" value={inputs.greenCreditPct} unit="%" max={100} onChange={setInput} />
                            <NumberInput label="Prisspænd" hint="gns. dagligt høj–lav" id="arbSpread" value={inputs.arbSpread} unit="kr/MWh" step={10} onChange={setInput} />
                        </div>
                        <NumberInput label="Arbitrage-cyklusser" hint="fulde op/ned pr. år" id="arbCycles" value={inputs.arbCycles} unit="/år" step={10} onChange={setInput} />
                        <div className={classes.twoCol}>
                            <NumberInput label="Service-aftale" hint="kr/MW · udstyr" id="serviceKr" value={inputs.serviceKr} unit="kr/MW" step={1000} onChange={setInput} />
                            <NumberInput label="Forsikring m.m." hint="kr/MW" id="insuranceKr" value={inputs.insuranceKr} unit="kr/MW" step={1000} onChange={setInput} />
                        </div>
                    </div>
                </div>

                {/* RIGHT: Results */}
                <div className={classes.resultsCol}>
                    {/* Headline */}
                    <div className={classes.headlineCard}>
                        <div className={classes.headlineLabel}>Forventet netto-indtægt &middot; år 1</div>
                        <div className={classes.headlineAmount}>
                            <span className={classes.headlineCurrency}>DKK</span>
                            {fmt(results.db2)}
                        </div>
                        <div className={classes.headlineSub}>
                            Opregulering: <strong>{fmt(results.baseUp)} DKK</strong>
                            <span className={classes.badge}>Aktiv</span>
                            &nbsp;&nbsp;
                            Nedregulering: <strong>{fmt(results.baseDown)} DKK</strong>
                            <span className={`${classes.badge} ${!loadbanks ? classes.badgeOff : ''}`}>
                                {loadbanks ? 'Aktiv' : 'Deaktiveret'}
                            </span>
                        </div>
                    </div>

                    {/* KPI cards */}
                    <div className={classes.kpiRow}>
                        {kpis.map((k) => (
                            <div key={k.label} className={classes.kpiCard}>
                                <div className={classes.kpiLabel}>{k.label}</div>
                                <div className={classes.kpiValue}>{fmt(k.value)} <span className={classes.kpiUnit}>DKK</span></div>
                                <div className={classes.kpiSub}>{k.sub}</div>
                            </div>
                        ))}
                    </div>

                    {/* P&L breakdown */}
                    <div className={classes.tableCard}>
                        <div className={classes.cardHeader}>
                            <span className={classes.cardNum}>02</span>
                            <span className={classes.cardTitle}>Indtægts- &amp; omkostningsspecifikation &middot; år 1</span>
                        </div>
                        <table className={classes.pnlTable}>
                            <thead>
                                <tr>
                                    <th>Linje</th>
                                    <th className={classes.numTh}>DKK</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.pnlRows.map((row, i) => (
                                    <tr
                                        key={i}
                                        className={
                                            row.kind === 'total' ? classes.trTotal :
                                            row.kind === 'subtotal' ? classes.trSubtotal :
                                            row.kind === 'disabled' ? classes.trDisabled : ''
                                        }
                                    >
                                        <td>
                                            {row.kind === 'subtotal' || row.kind === 'total'
                                                ? <strong>{row.label}</strong>
                                                : row.label}
                                            {row.note && <em className={classes.noteText}> {row.note}</em>}
                                        </td>
                                        <td className={`${classes.numTd} ${
                                            row.kind !== 'disabled'
                                                ? row.value >= 0 ? classes.posVal : classes.negVal
                                                : ''
                                        }`}>
                                            {fmtSigned(row.value)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Disclaimer */}
                    <div className={classes.disclaimerCard}>
                        <div className={classes.disclaimerTitle}>Ansvarsfraskrivelse og forbehold</div>
                        <p>
                            <strong>1. Ingen garanti.</strong> De viste tal udgør udelukkende uforpligtende
                            fremskrivninger og overslag. De er ikke et tilbud, et tilsagn, en garanti, en
                            prognose eller en forsikring om fremtidige indtægter, og der kan ikke udledes
                            nogen ret eller forventning af dem.
                        </p>
                        <p>
                            <strong>2. Datagrundlag.</strong> Beregningerne bygger på de seneste 12 måneders
                            gennemsnitlige, historiske indtægter fra systemydelsesmarkederne (aFRR/PICASSO)
                            baseret på offentligt tilgængelige data fra <strong>Energinet</strong>.
                            Historiske resultater er ingen indikation for fremtidige resultater. Data kan
                            indeholde fejl, mangler eller ændres med tilbagevirkende kraft uden varsel.
                        </p>
                        <p>
                            <strong>3. Forhold uden for vores kontrol.</strong> Faktiske indtægter afhænger
                            bl.a. af markedspriser, aktiveringsvolumen, tilgængelighed, budstrategi,
                            driftsforhold, tekniske forhold, vejr, ændringer i lovgivning, regulering,
                            markedsdesign og afgifter samt aggregator- og modpartsforhold — alt sammen
                            uden for S.C. Nordics kontrol.
                        </p>
                        <p>
                            <strong>4. Fuld ansvarsfraskrivelse.</strong> Materialet leveres "som det er og
                            forefindes", uden nogen form for udtrykkelig eller stiltiende garanti, herunder
                            for nøjagtighed, fuldstændighed eller egnethed til et bestemt formål. S.C. Nordic,
                            dets ejere, ledelse, medarbejdere og samarbejdspartnere fraskriver sig i videst
                            muligt omfang efter gældende ret ethvert ansvar for direkte, indirekte,
                            tilfældige, følge- eller pønalt begrundede tab — herunder tab af fortjeneste,
                            besparelser, data eller goodwill — der måtte opstå som følge af brug af eller
                            tillid til disse tal.
                        </p>
                        <p>
                            <strong>5. Ikke rådgivning.</strong> Materialet udgør ikke investerings-,
                            finansiel, juridisk, skattemæssig eller anden professionel rådgivning og bør
                            ikke lægges til grund for beslutninger. Enhver beslutning træffes på modtagerens
                            eget ansvar og risiko, og der opfordres til at indhente uafhængig professionel
                            rådgivning. Intet kontraktforhold eller nogen forpligtelse opstår på baggrund af
                            dette materiale.
                        </p>
                    </div>
                </div>
            </div>

            <div className={classes.footer}>
                <span>Kilde: Energinet &middot; 12-måneders rullende gennemsnit. Se ansvarsfraskrivelse ovenfor.</span>
                <span>v2.5 &middot; 17 sep 2026</span>
            </div>
        </div>
    );
};

export default RevenueCalculator;
